import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SampleBanner } from "@/components/SampleBanner";
import { createMarketListing, fetchConfig } from "@/lib/api";

const NONE = "__none__";

const EMPTY = {
    handle: "", name: "", niche: "", city: "", platforms: "",
    followers: "", avg_views_30d: "", avg_watch_time_sec: "", engagement_rate: "",
    next_event: "", next_event_date: "", sport_result: "", bio: "",
    title: "", slot_type: "", event_name: "", event_date: "",
    starting_bid_usd: "", min_increment_usd: "25", ends_at: "",
    deliverables: "", proof_required: "",
};

/** The five metrics a listing cannot publish without (mirrors the server gate). */
function missingMetrics(form) {
    const missing = [];
    if (!form.handle.trim()) missing.push("platform handle");
    if (!form.platforms.trim()) missing.push("platforms");
    if (!(Number(form.followers) > 0)) missing.push("followers");
    if (!(Number(form.avg_views_30d) > 0)) missing.push("avg views (30 days)");
    if (!form.niche.trim()) missing.push("niche");
    if (!form.next_event_date) missing.push("next event date");
    return missing;
}

/** Create a sponsor slot. Publish is blocked until the creator metrics exist. */
export default function NewListing() {
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    const config = useQuery({ queryKey: ["config"], queryFn: fetchConfig });
    const slotTypes = config.data?.market_slot_types || [];
    const minBid = config.data?.market_min_bid_usd || 25;

    const set = (key) => (e) => {
        const value = e && e.target ? e.target.value : e;
        setForm((f) => ({ ...f, [key]: value }));
    };

    const missing = useMemo(() => missingMetrics(form), [form]);
    const bidOk = Number(form.starting_bid_usd) >= minBid && Number(form.min_increment_usd) >= 1;
    const endsOk = form.ends_at && new Date(form.ends_at).getTime() > Date.now();
    const canPublish = missing.length === 0 && !!form.title.trim() && !!form.slot_type && bidOk && endsOk;

    const submit = async () => {
        if (!canPublish || busy) return;
        setBusy(true);
        try {
            const data = await createMarketListing({
                creator: {
                    handle: form.handle,
                    name: form.name || form.handle,
                    niche: form.niche,
                    city: form.city,
                    platforms: form.platforms,
                    followers: Number(form.followers) || 0,
                    avg_views_30d: Number(form.avg_views_30d) || 0,
                    avg_watch_time_sec: Number(form.avg_watch_time_sec) || 0,
                    engagement_rate: Number(form.engagement_rate) || 0,
                    next_event: form.next_event,
                    next_event_date: form.next_event_date || null,
                    sport_result: form.sport_result,
                    bio: form.bio,
                },
                title: form.title,
                slot_type: form.slot_type,
                event_name: form.event_name || form.next_event,
                event_date: form.event_date || form.next_event_date || null,
                starting_bid_usd: Number(form.starting_bid_usd),
                min_increment_usd: Number(form.min_increment_usd) || 25,
                ends_at: new Date(form.ends_at).toISOString(),
                deliverables: form.deliverables,
                proof_required: form.proof_required,
            });
            toast.success("Slot live — metrics saved, auction open");
            navigate(`/a/${data.listing.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not publish the listing");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
            <SampleBanner className="mb-6" />
            <h1 className="font-heading text-3xl font-bold tracking-tight">List a sponsor slot</h1>
            <p className="text-muted-foreground mt-2 mb-6 text-sm sm:text-base">
                Concrete inventory with real metrics attached. Sponsors bid on the deliverable, not
                on a promise.
            </p>

            {missing.length ? (
                <div
                    className="mb-6 rounded-lg border border-heat/40 bg-heat/10 px-3 py-2.5 text-sm text-heat flex gap-2"
                    data-testid="publish-blocked"
                >
                    <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                        <strong>Publish blocked</strong> — fill the creator metrics first: {missing.join(", ")}.
                    </span>
                </div>
            ) : null}

            <div className="space-y-6">
                <Card className="bg-card border-border p-5">
                    <h2 className="font-heading text-lg font-bold mb-4">1 · Creator metrics</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-handle">Platform handle *</Label>
                            <Input id="nl-handle" data-testid="nl-handle" value={form.handle} onChange={set("handle")} placeholder="@hyroxmaya" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-name">Display name</Label>
                            <Input id="nl-name" value={form.name} onChange={set("name")} placeholder="Maya Chen" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-niche">Niche *</Label>
                            <Input id="nl-niche" data-testid="nl-niche" value={form.niche} onChange={set("niche")} placeholder="HYROX, running, strength…" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-city">City</Label>
                            <Input id="nl-city" value={form.city} onChange={set("city")} placeholder="Berlin" />
                        </div>
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label htmlFor="nl-platforms">Platforms * (comma-separated)</Label>
                            <Input id="nl-platforms" data-testid="nl-platforms" value={form.platforms} onChange={set("platforms")} placeholder="instagram,tiktok" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-followers">Followers *</Label>
                            <Input id="nl-followers" data-testid="nl-followers" type="number" min={0} value={form.followers} onChange={set("followers")} placeholder="84200" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-views">Avg views last 30 days *</Label>
                            <Input id="nl-views" data-testid="nl-views" type="number" min={0} value={form.avg_views_30d} onChange={set("avg_views_30d")} placeholder="31200" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-watch">Avg watch time (seconds)</Label>
                            <Input id="nl-watch" type="number" min={0} value={form.avg_watch_time_sec} onChange={set("avg_watch_time_sec")} placeholder="18" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-engagement">Engagement rate (0–1)</Label>
                            <Input id="nl-engagement" type="number" step="0.001" min={0} max={1} value={form.engagement_rate} onChange={set("engagement_rate")} placeholder="0.041" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-event">Next event</Label>
                            <Input id="nl-event" value={form.next_event} onChange={set("next_event")} placeholder="HYROX Berlin" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-event-date">Next event date *</Label>
                            <Input id="nl-event-date" data-testid="nl-event-date" type="date" value={form.next_event_date} onChange={set("next_event_date")} />
                        </div>
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label htmlFor="nl-result">Race result / sport score</Label>
                            <Input id="nl-result" value={form.sport_result} onChange={set("sport_result")} placeholder="Berlin 2025 1:18:42" />
                        </div>
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label htmlFor="nl-bio">Short bio</Label>
                            <Textarea id="nl-bio" value={form.bio} onChange={set("bio")} rows={2} placeholder="HYROX racer. Kit and race-week content." />
                        </div>
                    </div>
                </Card>

                <Card className="bg-card border-border p-5">
                    <h2 className="font-heading text-lg font-bold mb-4">2 · The slot</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label htmlFor="nl-title">Title *</Label>
                            <Input id="nl-title" data-testid="nl-title" value={form.title} onChange={set("title")} placeholder="Chest kit — HYROX Berlin" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Slot type *</Label>
                            <Select value={form.slot_type || NONE} onValueChange={(v) => setForm((f) => ({ ...f, slot_type: v === NONE ? "" : v }))}>
                                <SelectTrigger data-testid="nl-slot-type"><SelectValue placeholder="Pick the inventory type" /></SelectTrigger>
                                <SelectContent>
                                    {slotTypes.map((s) => (
                                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-event-name">Event name</Label>
                            <Input id="nl-event-name" value={form.event_name} onChange={set("event_name")} placeholder="HYROX Berlin" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-slot-date">Event date</Label>
                            <Input id="nl-slot-date" type="date" value={form.event_date} onChange={set("event_date")} />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-ends">Auction ends *</Label>
                            <Input id="nl-ends" data-testid="nl-ends" type="datetime-local" value={form.ends_at} onChange={set("ends_at")} />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-start">Starting bid (USD) *</Label>
                            <Input id="nl-start" data-testid="nl-starting-bid" type="number" min={minBid} value={form.starting_bid_usd} onChange={set("starting_bid_usd")} placeholder={String(minBid)} />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="nl-inc">Min increment (USD)</Label>
                            <Input id="nl-inc" type="number" min={1} value={form.min_increment_usd} onChange={set("min_increment_usd")} />
                        </div>
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label htmlFor="nl-deliverables">Deliverables (comma-separated)</Label>
                            <Textarea id="nl-deliverables" value={form.deliverables} onChange={set("deliverables")} rows={2} placeholder="Logo on race kit chest, 3 race-day posts, 1 recap Reel" />
                        </div>
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label htmlFor="nl-proof">Proof required</Label>
                            <Input id="nl-proof" value={form.proof_required} onChange={set("proof_required")} placeholder="race photos + post URLs" />
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4">
                        Minimum starting bid {`$${minBid}`}. Bids below {`$${minBid}`} are rejected by the auction.
                    </p>
                </Card>

                <Button
                    data-testid="nl-publish"
                    onClick={submit}
                    disabled={!canPublish || busy}
                    className="w-full h-12 bg-brand text-primary-foreground hover:bg-brand-dim font-semibold text-base"
                >
                    {busy ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Publishing…</>
                    ) : missing.length ? (
                        "Fill metrics to publish"
                    ) : (
                        "Publish auction"
                    )}
                </Button>
            </div>
        </div>
    );
}


